import { type PatientUser, type Person } from '~/shared/types'
import type { UserRetrieveResponse } from '@fireflyhealth/api'

/**
 * The full name to show for a person in a list row.
 *
 * A prospect has a preferred name and no legal name until they convert, so a row built from
 * `first_name` + `last_name` alone renders as a bare space. Absent parts are dropped rather
 * than interpolated, so a partial record never renders "null" or a stray space.
 *
 * `patientName` is the sibling for a single patient and is deliberately the *greeting* name —
 * its caller renders chat macros. This one is the full name for a row.
 */
export const personRowName = (person: {
  first_name?: string | null
  last_name?: string | null
  preferred_name?: string | null
}): string => {
  const legalName = [person.first_name, person.last_name].filter(Boolean).join(' ').trim()
  return legalName || person.preferred_name?.trim() || ''
}

/** Use the patient's preferred name if it exists */
export const patientName = (
  patient:
    | (Pick<UserRetrieveResponse, 'first_name'> & {
        person: Pick<Person, 'preferred_name'>
      })
    | undefined,
): string => {
  return patient?.person?.preferred_name || patient?.first_name || ''
}

export const hasCareEnrollmentRequiredData = (patient: PatientUser): boolean => {
  //  validate if patient has all the required data to enroll in care program
  // First name, last name, dob, sex, member id, address, state
  if (!patient?.person) {
    return false
  }
  const person = patient.person
  if (!person.first_name || !person.last_name || !person.dob || !person.sex) {
    return false
  }
  if (!person?.insurance_info) {
    return false
  }
  const insuranceInfo = person.insurance_info
  if (
    !insuranceInfo?.member_id ||
    !insuranceInfo?.addresses ||
    insuranceInfo?.addresses.length === 0
  ) {
    return false
  }
  const address = insuranceInfo.addresses[0]
  if (!address?.state) {
    return false
  }
  return true
}
